/**
 * GradientButton - Core Reusable Gradient Button Component
 * 
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * Provides a premium button with gradient background and special effects.
 * Inspired by modern SaaS designs.
 * 
 * Mobile Optimizations:
 * - WCAG 2.1 AA compliant touch targets (min 44x44px)
 * - Respects prefers-reduced-motion
 * - Touch-optimized interactions
 * - Responsive sizing
 */

'use client';

import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

export interface GradientButtonProps {
  /**
   * Button content
   */
  children: ReactNode;
  
  /**
   * Click handler
   */
  onClick?: () => void;
  
  /**
   * Button type
   */
  type?: 'button' | 'submit' | 'reset';
  
  /**
   * Custom className
   */
  className?: string;
  
  /**
   * Button size
   */
  size?: 'sm' | 'md' | 'lg';
  
  /**
   * Show icon
   */
  icon?: ReactNode;
  
  /**
   * Icon position
   */
  iconPosition?: 'left' | 'right';
  
  /**
   * Disabled state
   */
  disabled?: boolean;
  
  /**
   * Aria label
   */
  ariaLabel?: string;
  
  /**
   * Enable shine effect
   */
  shine?: boolean;
  
  /**
   * Enable hover scale
   */
  hoverScale?: boolean;
}

/**
 * Core Gradient Button Component
 * 
 * Premium button with gradient background and special effects.
 * Themes can customize via className.
 */
export function GradientButton({
  children,
  onClick,
  type = 'button',
  className = '',
  size = 'md',
  icon,
  iconPosition = 'right',
  disabled = false,
  ariaLabel,
  shine = true,
  hoverScale = true,
}: GradientButtonProps) {
  const prefersReducedMotion = useReducedMotion();
  
  // Mobile-optimized size classes with WCAG 2.1 AA touch targets
  const sizeClasses = {
    sm: 'px-4 md:px-5 py-2.5 md:py-2 min-h-[44px] text-xs md:text-sm',
    md: 'px-5 md:px-6 py-3 min-h-[48px] text-sm md:text-base',
    lg: 'px-6 md:px-8 py-3.5 md:py-4 min-h-[52px] text-base md:text-lg',
  };

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      whileHover={!prefersReducedMotion && hoverScale && !disabled ? { scale: 1.02 } : {}}
      whileTap={!prefersReducedMotion && hoverScale && !disabled ? { scale: 0.98 } : {}}
      className={`
        gradient-button
        ${sizeClasses[size]}
        ${disabled ? 'gradient-button-disabled' : ''}
        touch-manipulation active:opacity-90
        ${className}
      `}
    >
      {/* Shine Effect */}
      {shine && !disabled && (
        <div className="gradient-button-shine" />
      )}
      
      {/* Content */}
      <span className="gradient-button-content">
        {icon && iconPosition === 'left' && (
          <span className="gradient-button-icon gradient-button-icon-left">
            {icon}
          </span>
        )}
        
        <span className="gradient-button-text">
          {children}
        </span>
        
        {icon && iconPosition === 'right' && (
          <span className="gradient-button-icon gradient-button-icon-right">
            {icon}
          </span>
        )}
      </span>
    </motion.button>
  );
}

